<script lang="ts">
  interface Option {
    value: string;
    label: string;
    disabled?: boolean;
  }
  interface Props {
    id: string;
    label: string;
    value: string;
    options: Option[];
    disabled?: boolean;
    error?: string | undefined;
    hideLabel?: boolean;
    /**
     * `sm` is the dense variant for grids of many selects: a shorter control, and the
     * label sits beside it rather than above so each grid cell stays one line tall.
     */
    size?: "md" | "sm";
    /** Marks a value that differs from its inherited default. */
    highlighted?: boolean;
    onchange?: (value: string) => void;
  }
  let {
    id,
    label,
    value = $bindable(),
    options,
    disabled = false,
    error,
    hideLabel = false,
    size = "md",
    highlighted = false,
    onchange
  }: Props = $props();
  const errorId = $derived(`${id}-error`);
</script>

<div class="select-wrap" class:sm={size === "sm"} class:highlighted>
  <label for={id} class:sr-only={hideLabel}>{label}</label>
  <div class="select-shell" class:invalid={!!error}>
    <select
      {id}
      bind:value
      {disabled}
      aria-invalid={error ? "true" : undefined}
      aria-describedby={error ? errorId : undefined}
      onchange={(e) => onchange?.((e.currentTarget as HTMLSelectElement).value)}
    >
      {#each options as option (option.value)}
        <option value={option.value} disabled={option.disabled}>{option.label}</option>
      {/each}
    </select>
    <svg viewBox="0 0 16 16" aria-hidden="true" class="chev"><path d="M4 6l4 4 4-4" stroke="currentColor" fill="none" stroke-width="1.5" /></svg>
  </div>
  {#if error}<p id={errorId} class="error" role="alert">{error}</p>{/if}
</div>

<style>
  .select-wrap {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  label {
    font-size: 13px;
    font-weight: 600;
    color: var(--muted);
  }
  .select-shell {
    position: relative;
    display: flex;
    align-items: center;
  }
  select {
    appearance: none;
    width: 100%;
    background: var(--bg-2);
    border: 1px solid var(--line-soft);
    color: var(--text);
    border-radius: var(--radius-btn);
    padding: 11px 36px 11px 14px;
    font-family: inherit;
    font-size: 15px;
    min-height: 42px;
    cursor: pointer;
    transition: border-color var(--dur-3) var(--ease), box-shadow var(--dur-3) var(--ease);
  }
  /* Site .field:focus. The --ring-soft halo is decoration; the :focus-visible
   * outline from app.css remains the keyboard focus indicator. */
  select:focus {
    border-color: var(--accent);
    box-shadow: var(--ring-soft);
  }
  select:focus:not(:focus-visible) {
    outline: none;
  }
  .select-shell.invalid select {
    border-color: var(--err-line);
  }
  .chev {
    position: absolute;
    right: 12px;
    width: 16px;
    height: 16px;
    color: var(--muted);
    pointer-events: none;
  }
  .sm {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px 10px;
  }
  .sm label {
    flex: 1 1 0;
    min-width: 0;
    font-size: 12.5px;
    font-weight: 500;
    color: var(--text);
    overflow-wrap: anywhere;
  }
  .sm .select-shell {
    flex: 0 0 auto;
  }
  .sm select {
    width: auto;
    min-width: 128px;
    min-height: 32px;
    padding: 5px 30px 5px 10px;
    font-size: 13px;
  }
  .sm .chev {
    right: 9px;
    width: 14px;
    height: 14px;
  }
  .sm .error {
    flex-basis: 100%;
  }
  .highlighted select {
    border-color: var(--accent-line);
    background: var(--accent-fill);
    color: var(--heading);
  }
  .error {
    font-size: 12.5px;
    color: var(--err-text);
    margin: 0;
  }
</style>
